// @flow

import { extendObservable, action, runInAction } from "mobx";
import ModalState from "./ModalState";

export const MARKUP_MODE = {
  SUGGESTED: "suggested",
  PAYROLL: "payroll",
  STAFFAUG: "staffaug",
  CUSTOM: "custom",
};

// Backs the "Download PDF" markup-basis modal (PAY-100/PAY-101). Four modes:
// - suggested: uses each level's own actual stored markup, no fields shown
// - payroll / staffaug: overrides just that one side, the other stays on
//   the level's own stored markup
// - custom: overrides both
// None of this touches the rate card's own data — it's a print-time-only
// calculation. The currency toggle asks the backend to return USD-converted
// values (same convertTo mechanism the on-screen currency filter already
// uses); the backend converts each search based on its own currency, so
// this works correctly even across a rate card that mixes currencies —
// opening the modal only checks whether there's anything to convert at all.
export default class MarkupOverrideState {
  modalState: ModalState;
  mode: string;
  payrollInput: string;
  staffingInput: string;
  error: ?string;
  searchIds: ?Array<string>;
  loading: boolean;
  hasNonUsdCurrency: boolean;
  convertToUsd: boolean;

  prepareFunction: (searchIds: ?Array<string>) => Promise<?{ hasNonUsdCurrency: boolean }>;
  printFunction: (
    searchIds: ?Array<string>,
    payrollMarkupPct: ?number,
    staffingMarkupPct: ?number,
    convertToUsd: boolean
  ) => void;

  open: (searchIds: ?Array<string>) => Promise<void>;
  setMode: (string) => void;
  toggleCurrency: () => void;
  onChangePayrollInput: (Event) => void;
  onChangeStaffingInput: (Event) => void;
  onConfirm: (Event) => void;
  onCancel: (Event) => void;

  constructor(
    prepareFunction: (searchIds: ?Array<string>) => Promise<?{ hasNonUsdCurrency: boolean }>,
    printFunction: (
      searchIds: ?Array<string>,
      payrollMarkupPct: ?number,
      staffingMarkupPct: ?number,
      convertToUsd: boolean
    ) => void
  ) {
    extendObservable(this, {
      mode: MARKUP_MODE.SUGGESTED,
      payrollInput: "",
      staffingInput: "",
      error: null,
      searchIds: null,
      loading: false,
      hasNonUsdCurrency: false,
      convertToUsd: false,
    });

    this.prepareFunction = prepareFunction;
    this.printFunction = printFunction;
    this.modalState = new ModalState();

    this.open = action(this.open.bind(this));
    this.setMode = action(this.setMode.bind(this));
    this.toggleCurrency = action(this.toggleCurrency.bind(this));
    this.onChangePayrollInput = action(this.onChangePayrollInput.bind(this));
    this.onChangeStaffingInput = action(this.onChangeStaffingInput.bind(this));
    this.onConfirm = action(this.onConfirm.bind(this));
    this.onCancel = action(this.onCancel.bind(this));
  }

  async open(searchIds: ?Array<string>) {
    this.searchIds = searchIds;
    this.mode = MARKUP_MODE.SUGGESTED;
    this.payrollInput = "";
    this.staffingInput = "";
    this.error = null;
    this.loading = true;
    this.hasNonUsdCurrency = false;
    this.convertToUsd = false;
    this.modalState.showModal();

    const result = await this.prepareFunction(searchIds);

    runInAction(() => {
      this.loading = false;
      if (!result) {
        this.error = "Couldn't load this rate card's data. Please close and try again.";
        return;
      }
      this.hasNonUsdCurrency = result.hasNonUsdCurrency;
    });
  }

  setMode(mode: string) {
    this.mode = mode;
    this.error = null;
  }

  toggleCurrency() {
    this.convertToUsd = !this.convertToUsd;
  }

  onChangePayrollInput(e: Event & { target: HTMLInputElement }) {
    this.payrollInput = e.target.value;
  }

  onChangeStaffingInput(e: Event & { target: HTMLInputElement }) {
    this.staffingInput = e.target.value;
  }

  // Returns a number for a valid non-negative entry, null if left blank, or
  // throws with a user-facing message on invalid input.
  static parseMarkupInput(label: string, raw: string): ?number {
    const trimmed = raw.trim();
    if (trimmed === "") return null;

    const value = Number(trimmed);
    if (Number.isNaN(value) || value < 0) {
      throw new Error(`${label} markup must be a non-negative number, or left blank.`);
    }
    return value;
  }

  onConfirm(e: Event) {
    if (this.loading) return;

    let payrollMarkupPct = null;
    let staffingMarkupPct = null;
    try {
      if (this.mode === MARKUP_MODE.PAYROLL || this.mode === MARKUP_MODE.CUSTOM) {
        payrollMarkupPct = MarkupOverrideState.parseMarkupInput("Payroll", this.payrollInput);
      }
      if (this.mode === MARKUP_MODE.STAFFAUG || this.mode === MARKUP_MODE.CUSTOM) {
        staffingMarkupPct = MarkupOverrideState.parseMarkupInput("Staff Aug", this.staffingInput);
      }
    } catch (err) {
      this.error = err.message;
      return;
    }

    this.error = null;
    this.modalState.hideModal();
    this.printFunction(this.searchIds, payrollMarkupPct, staffingMarkupPct, this.convertToUsd);
  }

  onCancel(e: Event) {
    this.modalState.hideModal();
  }
}
